import { ReactNode } from 'react';

import { Link, LinkProps } from '../Link';
import { Box } from '../Box';
import { Icon } from '../Icon';

import { useStyles } from './RedirectCard.styles';

/**
 * Свойства компонента {@link RedirectCard}
 */
export type RedirectCardProps = LinkProps & {
  /**
   * Обработчик клика для карты.
   */
  onClick: () => void;

  /**
   * Логическое значение, указывающее, должна ли карта занимать всю ширину своего контейнера.
   *
   * @defaultValue `false`
   */
  fullWidth?: boolean;

  /**
   * Дочерние элементы, которые будут отображены внутри карты.
   */
  children: ReactNode;
};

/**
 * Компонент RedirectCard, который отображает кликабельную карту с необязательным стилем полной ширины.
 * Использует иконку Material-UI ArrowForwardIosIcon для указания направления перенаправления.
 *
 * @public
 * @param to - Путь для ссылки при использовании react-router.
 * @param href - URL для ссылки при использовании обычного якорного тега.
 * @param target - Атрибут target для якорного тега.
 * @param onClick - Обработчик клика для карты.
 * @param fullWidth - Логическое значение, указывающее, должна ли карта занимать всю ширину своего контейнера.
 * @param children - Дочерние элементы, которые будут отображены внутри карты.
 * @returns Компонент RedirectCard.
 *
 * @remarks
 *
 * Ссылка на свойства компонента {@link RedirectCardProps}
 */
export const RedirectCard = ({
  children,
  fullWidth = false,
  ...linkProps
}: RedirectCardProps) => {
  const { classes } = useStyles();

  return (
    <Link stroke={false} variant="simple" {...linkProps}>
      <Box
        className={classes.card}
        display="flex"
        alignItems="center"
        gap="8"
        p="16"
        bgcolor="bg.greyscale.primary"
        radius="l"
        width={fullWidth ? '100%' : 'auto'}
        testId="redirect-card-test-id"
      >
        {children}

        <Box display="flex" alignItems="center" ml="auto" my="-2" mr="-2">
          <Icon
            name="chevron_right_outline"
            size="20"
            color="stroke.secondary"
            data-testid="arrow-forward-ios-icon-test-id"
          />
        </Box>
      </Box>
    </Link>
  );
};
